import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { currencyFormatter } from '../../../services/helperService';
import { getRankBadgeStyle, tertiaryText, secondaryText, primaryText } from '../../../styling/stylingHelper';
import { NoDataInPeriod } from '../../ui/NoDataInPeriod';

export interface RankedBarRow {
  id: string;
  label: string;
  /** Value driving the right-aligned amount. */
  value: number;
  /** Share of the total, 0–1 (drives the proportional bar width). */
  share: number;
  /** Absolute €-change vs the previous count, or null/undefined to hide the pill. */
  changeValue?: number | null;
  /** Secondary text next to the label on the top line (e.g. "25 products"). */
  subline?: React.ReactNode;
  /** Optional pill rendered top-right next to the chevron (e.g. "27 below minimum"). */
  rightPill?: React.ReactNode;
  /** When set, the whole row is clickable. */
  onClick?: () => void;
  /** Optional `data-onboarding` id on the row. */
  onboardingId?: string;
  /** Highlights the row (e.g. active click-to-filter selection). */
  active?: boolean;
  /** When set, the row gets a chevron toggle revealing these indented sub-rows. */
  subRows?: RankedBarRow[];
  /** Rest bucket (e.g. "Unattributed"): no rank badge, muted label and grey bar. */
  muted?: boolean;
}

const BAR_COLORS = {
  purple: '#6938EF',
  green: '#32D583',
} as const;

const axisFormatter = new Intl.NumberFormat(currencyFormatter.resolvedOptions().locale, {
  style: 'currency',
  currency: currencyFormatter.resolvedOptions().currency,
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function niceTicks(max: number): number[] {
  if (max <= 0) return [0];
  const padded = max * 1.08;
  const mag = Math.pow(10, Math.floor(Math.log10(padded)));
  const stepCandidates = [1, 2, 2.5, 5, 10].map((n) => n * mag);
  const step = stepCandidates.find((s) => padded / s <= 7) ?? mag;
  const top = Math.ceil(padded / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(v);
  return ticks;
}

/** Signed-currency change pill (green up / red down), mirroring the StatCard pill. */
const CurrencyChangePill: React.FC<{ changeValue: number }> = ({ changeValue }) => {
  const up = changeValue >= 0;
  return (
    <span
      className={`text-[14px] leading-[26px] tracking-[-0.01rem] font-semibold px-1 sm:px-2 rounded-[5px] whitespace-nowrap ${up
          ? 'bg-[#069A6E1A] text-[#069A6E] dark:bg-[#16A34A1A] dark:text-[#16A34A]'
          : 'bg-[#D92D201A] text-[#D92D20] dark:bg-[#B91C1C40] dark:text-[#F87171]'
        }`}
    >
      {up ? '+' : '-'}{currencyFormatter.format(Math.abs(changeValue))}
    </span>
  );
};

const Row: React.FC<{ row: RankedBarRow; isSubRow?: boolean; rank?: number; barColor: string }> = ({
  row,
  isSubRow,
  rank,
  barColor,
}) => {
  const clickable = typeof row.onClick === 'function';
  const expandable = (row.subRows?.length ?? 0) > 0;
  const [expanded, setExpanded] = useState(false);
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <div className={cn(isSubRow && 'pl-5')}>
      <div
        onClick={row.onClick}
        data-onboarding={row.onboardingId}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={
          clickable
            ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                row.onClick!();
              }
            }
            : undefined
        }
        className={cn(
          'flex flex-col py-2',
          clickable && 'cursor-pointer transition-colors',
          row.active && 'bg-green-50 dark:bg-green-900/20 ring-1 ring-textGreen dark:ring-textGreen-dark'
        )}
      >
        {/* ── Row 1: rank + label + subline  |  pill + chevron ── */}
        <div className="flex items-center justify-between gap-2">
          {/* Left: rank badge + expand chevron + label + subline */}
          <div className="flex items-center gap-1.5 min-w-0">
            {typeof rank === 'number' && !isSubRow && !row.muted ? (
              <span
                className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full flex-shrink-0 text-white dark:text-white text-[12px] font-bold"
                style={getRankBadgeStyle(rank)}
              >
                {rank}
              </span>
            ) : null}

            {expandable && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((v) => !v);
                }}
                className="p-0.5 rounded flex-shrink-0 text-textSecondary dark:text-textSecondary-dark hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Chevron className="w-4 h-4" />
              </button>
            )}

            <span
              className={cn(
                'text-[14px] sm:text-[18px] leading-5 font-semibold truncate',
                row.muted
                  ? cn(tertiaryText, 'italic')
                  : isSubRow
                  ? 'text-textSecondary dark:text-textSecondary-dark'
                  : 'text-textPrimary dark:text-textPrimary-dark'
              )}
            >
              {row.label}
            </span>

            {row.subline ? (
              <span className={cn('text-[14px] leading-5 font-medium flex-shrink-0 tracking-[-0.02rem] dark:text-[#667085]', secondaryText)}>
                {row.subline}
              </span>
            ) : null}
          </div>

          {/* Right: below-min pill + chevron arrow */}
          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            {row.rightPill ? row.rightPill : null}
            {clickable ? (
              <span className="flex-shrink-0 text-[#000000] dark:text-[#CBD5E1]" aria-hidden>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            ) : null}
          </div>
        </div>

        {/* ── Row 2: value  |  change pill + percentage ── */}
        <div className="flex items-center justify-between gap-2 mt-4">
          <span className={cn('text-[16px] font-semibold leading-5', primaryText)}>
            {currencyFormatter.format(row.value)}
          </span>

          <div className="flex items-center gap-1 flex-shrink-0">
            {typeof row.changeValue === 'number' && row.changeValue !== 0 && (
              <CurrencyChangePill changeValue={row.changeValue} />
            )}
            <span className={cn('text-[14px] leading-[20px] font-medium', tertiaryText)}>
              ({(row.share * 100).toFixed(1)}%)
            </span>
          </div>
        </div>

        {/* ── Progress bar ── */}
        <div
          className={cn(
            'w-full bg-[#D1D1D6] dark:bg-[#D1D1D6] rounded-full overflow-hidden mt-3',
            isSubRow ? 'h-[7px]' : 'h-[7px]'
          )}
        >
          <div
            className="h-full rounded-full"
            style={{ width: `${Math.min(row.share * 100, 100)}%`, backgroundColor: row.muted ? '#98A2B3' : barColor }}
          />
        </div>
      </div>

      {expandable && expanded && (
        <div className="flex flex-col gap-1">
          {row.subRows!.map((sub) => (
            <Row key={sub.id} row={sub} isSubRow barColor={barColor} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * A ranked list of value bars with share%, an optional €-change pill and a secondary
 * line — the "Value by inventory / section / category" template (ByRestaurantCard pattern).
 * Presentational only: labels + sublines are pre-translated by the caller.
 */
/** Magnitudes share one axis; signed amounts remain visible on touch screens. */
const AxisChart: React.FC<{ rows: RankedBarRow[]; barColor: keyof typeof BAR_COLORS }> = ({ rows, barColor }) => {
  const [inspectedRowId, setInspectedRowId] = useState<string | null>(null);
  const max = Math.max(1, ...rows.map((row) => Math.abs(row.value)));
  const ticks = niceTicks(max);
  const scaleMax = ticks[ticks.length - 1] || max;

  return (
    <div className="w-full pt-2">
      <div className="relative">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 left-[108px] right-0 top-0 border-y border-dotted border-gray-200 dark:border-gray-600 sm:left-[140px]"
        >
          {ticks.map((tick) => (
            <span
              key={tick}
              className={cn(
                'absolute inset-y-0 border-l border-gray-200 dark:border-gray-600',
                tick === 0 ? 'border-solid' : 'border-dotted'
              )}
              style={{ left: `${(tick / scaleMax) * 100}%` }}
            />
          ))}
        </div>
        {rows.map((row) => {
          const width = row.value === 0 ? 0 : Math.max(1, (Math.abs(row.value) / scaleMax) * 100);
          const signedValue = `${row.value > 0 ? '+' : ''}${currencyFormatter.format(row.value)}`;
          const content = (
            <>
              <span className="w-24 shrink-0 text-left sm:w-32">
                <span className={cn('block truncate text-[13px] font-semibold leading-5 sm:text-sm', row.muted ? tertiaryText : primaryText)} title={row.label}>
                  {row.label}
                </span>
                {row.subline && <span className={cn('mt-0.5 block text-[11px] leading-4 sm:text-xs', tertiaryText)}>{row.subline}</span>}
              </span>
              <span className="relative min-w-0 flex-1 py-3">
                <span
                  aria-hidden="true"
                  className={cn(
                    'block h-8 rounded-r-lg sm:h-9',
                    barColor === 'green' ? 'bg-[#3B9F87] dark:bg-[#48B89A]' : 'bg-[#6938EF] dark:bg-[#A78BFA]'
                  )}
                  style={{ width: `${width}%` }}
                />
                <span className={cn('mt-1 block text-right text-[10px] font-medium leading-3 sm:text-[11px]', row.value < 0 ? 'text-red-700 dark:text-red-400' : primaryText)}>
                  {signedValue}
                </span>
                <span
                  aria-hidden="true"
                  className={cn(
                    'pointer-events-none absolute top-8 z-10 -translate-x-1/2 rounded-lg bg-gray-800 px-3 py-2 text-left text-xs text-white shadow-md transition-opacity dark:bg-gray-950 dark:text-white',
                    inspectedRowId === row.id ? 'opacity-100' : 'opacity-0'
                  )}
                  style={{ left: `${Math.max(30, Math.min(width, 70))}%` }}
                >
                  <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-gray-800 dark:bg-gray-950" />
                  <span className="relative block whitespace-nowrap font-semibold">{signedValue}</span>
                  <span className="mt-1 block whitespace-nowrap text-amber-300 dark:text-amber-300">({(row.share * 100).toFixed(1)}%)</span>
                </span>
                <span className="sr-only">({(row.share * 100).toFixed(1)}%)</span>
              </span>
            </>
          );
          const className = cn(
            'relative flex w-full items-center gap-3 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 dark:focus-visible:ring-green-400',
            row.onClick && 'hover:bg-gray-50/50 dark:hover:bg-gray-800/50',
            row.active && 'bg-green-50/60 dark:bg-green-900/20'
          );
          const inspectionHandlers = {
            onMouseEnter: () => setInspectedRowId(row.id),
            onMouseLeave: () => setInspectedRowId(null),
            onFocus: () => setInspectedRowId(row.id),
            onBlur: () => setInspectedRowId(null),
            onTouchStart: () => setInspectedRowId(row.id),
            onKeyDown: (event: React.KeyboardEvent) => {
              if (event.key === 'Escape') setInspectedRowId(null);
            },
          };
          return row.onClick ? (
            <button
              key={row.id}
              type="button"
              className={className}
              onClick={row.onClick}
              data-onboarding={row.onboardingId}
              {...inspectionHandlers}
            >{content}</button>
          ) : (
            <div key={row.id} tabIndex={0} className={className} data-onboarding={row.onboardingId} {...inspectionHandlers}>{content}</div>
          );
        })}
      </div>
      <div aria-hidden="true" className={cn('relative ml-[108px] mt-2 h-5 text-[10px] font-medium sm:ml-[140px] sm:text-[11px]', tertiaryText)}>
        {ticks.map((tick, index) => (
          <span
            key={tick}
            className={cn(
              'absolute whitespace-nowrap',
              index === 0 ? '' : index === ticks.length - 1 ? '-translate-x-full' : '-translate-x-1/2',
              index > 0 && index < ticks.length - 1 && index % 2 !== 0 && 'hidden sm:block'
            )}
            style={{ left: `${(tick / scaleMax) * 100}%` }}
          >{axisFormatter.format(tick)}</span>
        ))}
      </div>
    </div>
  );
};

export const RankedValueBars: React.FC<{
  rows: RankedBarRow[];
  emptyText?: string;
  barColor?: keyof typeof BAR_COLORS;
  variant?: 'ranked' | 'chart';
}> = ({ rows, emptyText, barColor = 'purple', variant = 'ranked' }) => {
  const fill = BAR_COLORS[barColor];
  if (rows.length === 0) {
    return emptyText ? (
      <p className="text-[14px] text-textSecondary dark:text-textSecondary-dark text-center py-6">{emptyText}</p>
    ) : (
      <NoDataInPeriod />
    );
  }
  if (variant === 'chart') {
    return <AxisChart rows={rows} barColor={barColor} />;
  }
  return (
    <div className="w-full flex flex-col gap-3">
      {rows.map((row, idx) => (
        <Row key={row.id} row={row} rank={idx + 1} barColor={fill} />
      ))}
    </div>
  );
};

export default RankedValueBars;
